/**
 * "Applicant's Overview" — ONE card holding the whole household (client
 * 2026-08-27; the separate "Guarantor's Overview" section is gone). Tiles run
 * in household order: primary → co-applicants → children → guarantors →
 * everyone still invited. An invited co-applicant therefore sits at the end
 * until they accept, at which point they take their normal place.
 *
 * Each member tile carries their relationship, status, and a "View Report"
 * button that NAVIGATES to that member's individual v2 page (the drill-down
 * that replaces v1's tabs — COMBINED-V2-DATA-MAP §1). Children are listed
 * from the household property step (they have no application, so they show
 * age + DOB instead of a report link; their ✕ removes the row from every
 * applicant's step — client 2026-09-07). Pending invitees (invited, not yet
 * applied) render as muted tiles whose ✕ cancels the invitation.
 *
 * Status is shown through the avatar's coloured RING + corner BADGE — since
 * 2026-09-07 the DASHBOARD chip's `MemberAvatar` at tile size, so the ring
 * legend ("Status Circles": gray / blue / amber / red / green + decision
 * badges) is one derivation across dashboard, listing and this page. The
 * old second orange "guarantor recommended" ring (§7.2) is retired here as
 * it was on the dashboard. The top-right ✕
 * removes a member by DETACHING them from the group (removeMember intent —
 * group_id → NULL, optionally stamping declined/withdrawn; 2026-07-13),
 * gated by permission + decision-open; hidden only on the last ACTIVE
 * applicant (a household can't lose its only applicant).
 *
 * Section-header actions: "+ Add Applicant" / "Request Guarantor" — both
 * lock with the household decision (rule 2), like their server guards.
 */
import { Plus, X } from 'lucide-react'
import type { ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { CancelInviteButton } from '#app/components/application-detail-v2/GuarantorStatusList'
import { MemberAvatar } from '#app/components/application-detail-v2/vendor/MemberChip'
import {
	MEMBER_TOOLTIP_CLASS,
	StepPillTrail,
} from '#app/components/screening/StepPillTrail'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '#app/components/ui/tooltip'
import type { PendingInvitee } from '#app/utils/application-detail-v2/combined-types'
import { childAgeLabel } from '#app/utils/application-detail-v2/display'
import { preserveReturnTo } from '#app/utils/application-detail-v2/return-to'
import type {
	GroupMember,
	HouseholdChild,
} from '#app/utils/application-detail-v2/types'
import { useFormatDate } from '#app/utils/application-detail-v2/vendor/use-format-date'
import { cn } from '#app/utils/misc'
import { chipInput, memberInitials } from '../member-chip-input'
import { DETAIL_CARD, IconSquare } from '../primitives'

/** Role pill palette (client 2026-09-07): applicants and children green,
 * guarantors amber, invited red — the household's identity colours. */
const ROLE_PILL_CLASS = {
	applicant: 'bg-[#F3FAF6] text-[#16A34A]',
	child: 'bg-[#F3FAF6] text-[#16A34A]',
	guarantor: 'bg-[#FFF6EA] text-[#F59F0A]',
	invited: 'bg-[#FDF4F5] text-[#DC2634]',
} as const

function RolePill({
	tone,
	children,
}: {
	tone: keyof typeof ROLE_PILL_CLASS
	children: ReactNode
}) {
	return (
		<span
			className={cn(
				'rounded-full px-2.5 py-1 text-[12px] font-medium',
				ROLE_PILL_CLASS[tone],
			)}
		>
			{children}
		</span>
	)
}

function MemberTile({
	member,
	onView,
	onRemove,
}: {
	member: GroupMember
	onView: () => void
	/** Present only when this member may be removed (permission + not primary). */
	onRemove?: () => void
}) {
	const isGuarantor = member.applicationType === 'guarantor'
	const avatar = (
		<MemberAvatar member={chipInput(member)} size="xl" />
	)
	return (
		<div className="relative flex flex-col items-center gap-2 rounded-2xl border border-[#F0F0F0] px-5 py-5 text-center">
			{onRemove ? (
				<button
					type="button"
					onClick={onRemove}
					aria-label={`Remove ${member.displayName}`}
					className="absolute top-3 right-3 flex size-6 cursor-pointer items-center justify-center text-[#32302F] transition-opacity hover:opacity-60"
				>
					<X className="size-4.5" strokeWidth={2} />
				</button>
			) : null}
			{/* The avatar hover carries the SAME ID · Details · Income trail as the
			    dashboard / listing member chips (client 2026-07-13) — one shared
			    StepPillTrail + card-kit derivation, never a per-surface fork. */}
			{member.steps && member.steps.length > 0 ? (
				<Tooltip>
					<TooltipTrigger asChild>
						<span className="cursor-default">{avatar}</span>
					</TooltipTrigger>
					<TooltipContent side="top" className={MEMBER_TOOLTIP_CLASS}>
						<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
							<p className="text-base font-semibold text-[#32302F]">
								{member.displayName}
							</p>
							<span className="rounded-full bg-[#F4F4F2] px-2 py-0.5 text-[10px] font-semibold text-[#747474]">
								{member.relationshipLabel}
							</span>
						</div>
						<div className="mt-3">
							<StepPillTrail steps={member.steps} />
						</div>
					</TooltipContent>
				</Tooltip>
			) : (
				avatar
			)}
			<RolePill tone={isGuarantor ? 'guarantor' : 'applicant'}>
				{member.relationshipLabel}
			</RolePill>
			<span className="truncate text-[17px] font-semibold text-[#32302F]">
				{member.displayName}
			</span>
			<button
				type="button"
				onClick={onView}
				className="mt-1 w-full cursor-pointer rounded-full bg-[#32302F] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#1a1918]"
			>
				View Report
			</button>
		</div>
	)
}

/** A household child — no application, so no status ring and no report
 * link. The top-right ✕ (client 2026-09-07, reversing 2026-08-27's
 * "edit on the applicant's form" rule) opens RemoveChildDialog, which strips
 * the row from every applicant's property step. */
function ChildTile({
	child,
	onRemove,
}: {
	child: HouseholdChild
	/** Present only when the viewer may remove household children. */
	onRemove?: () => void
}) {
	const { formatShortDate } = useFormatDate()
	const age = childAgeLabel(child.dateOfBirth, new Date())
	const label = age ?? child.name?.trim() ?? 'Child'
	return (
		<div className="relative flex flex-col items-center gap-2 rounded-2xl border border-[#F0F0F0] px-5 py-5 text-center">
			{onRemove ? (
				<button
					type="button"
					onClick={onRemove}
					aria-label={`Remove ${child.name?.trim() || label}`}
					className="absolute top-3 right-3 flex size-6 cursor-pointer items-center justify-center text-[#32302F] transition-opacity hover:opacity-60"
				>
					<X className="size-4.5" strokeWidth={2} />
				</button>
			) : null}
			{/* The dashboard's child chip at tile size — green ring + face. */}
			<MemberAvatar
				member={{
					kind: 'child',
					status: '',
					hasStarted: false,
					startedAt: null,
					infoRequested: false,
					billingHeld: false,
					initials: '',
				}}
				size="xl"
			/>
			<RolePill tone="child">Child</RolePill>
			<span className="truncate text-[17px] font-semibold text-[#32302F]">
				{label}
			</span>
			<span className="mt-1 w-full rounded-full border border-[#F4F4F2] px-4 py-2 text-sm text-[#747474]">
				{child.dateOfBirth ? `DOB: ${formatShortDate(child.dateOfBirth)}` : '-'}
			</span>
		</div>
	)
}

function InviteeTile({
	invitee,
	cancelFromApplicationId,
}: {
	invitee: PendingInvitee
	/** Entry application id to post the TT-42 cancelInvitation intent against;
	 * undefined = no cancel affordance (no permission / household decided). */
	cancelFromApplicationId?: string
}) {
	const label =
		invitee.invitedName?.trim() || invitee.invitedEmail || 'Invited applicant'
	return (
		<div className="relative flex flex-col items-center gap-2 rounded-2xl border border-[#F0F0F0] bg-[#FBFDFD] px-5 py-5 text-center">
			{cancelFromApplicationId ? (
				// The SAME TT-42 control the guarantor status popover uses, drawn
				// as the tile's top-right ✕ (Figma 2026-09-07) like the member
				// tiles — the cancelled tile disappears on the action's
				// revalidation.
				<CancelInviteButton
					applicationId={cancelFromApplicationId}
					invitationId={invitee.id}
					variant="icon"
					invitedLabel={label}
				/>
			) : null}
			{/* The dashboard's invited chip at tile size — gray ring, initials. */}
			<MemberAvatar
				member={{
					kind: 'invited',
					status: '',
					hasStarted: false,
					startedAt: null,
					infoRequested: false,
					billingHeld: false,
					initials: memberInitials(label),
				}}
				size="xl"
			/>
			{/* Always "Invited" (client 2026-08-27) — the relationship they were
			    invited as is not what the landlord needs here; that they have not
			    started is. */}
			<RolePill tone="invited">Invited</RolePill>
			<span className="w-full truncate text-[14px] font-medium text-gray-600">
				{label}
			</span>
			{/* There is no report to open until they apply — shown disabled so the
			    tile keeps the same shape as the members around it. */}
			<button
				type="button"
				disabled
				aria-label={`${label} has not started their application`}
				className="mt-1 w-full cursor-not-allowed rounded-full bg-[#32302F]/50 px-4 py-2 text-sm font-medium text-white"
			>
				View Report
			</button>
		</div>
	)
}

function OverviewSection({
	title,
	action,
	children,
	emptyNote,
	hasContent,
}: {
	title: string
	action?: ReactNode
	children: ReactNode
	emptyNote: string
	hasContent: boolean
}) {
	return (
		<section className={cn(DETAIL_CARD, 'p-6 sm:p-7')}>
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex items-center gap-3">
					<IconSquare tone="lrose">
						<img
							src="/img/applicant-overview-icon.svg"
							alt=""
							aria-hidden="true"
							className="size-5"
						/>
					</IconSquare>
					<h2 className="text-[16px] font-semibold text-[#32302F]">{title}</h2>
				</div>
				{action}
			</div>
			{hasContent ? (
				<div className="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
					{children}
				</div>
			) : (
				<p className="mt-5 text-sm text-gray-500">{emptyNote}</p>
			)}
		</section>
	)
}

function SectionAction({
	icon,
	label,
	onClick,
}: {
	icon: ReactNode
	label: string
	onClick?: () => void
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="font-regular flex cursor-pointer items-center gap-1.5 rounded-lg border border-[#F0F0F0] px-5 py-2 text-sm text-[#747474] hover:text-[#1a1918] [&>svg]:size-4"
		>
			{icon}
			{label}
		</button>
	)
}

export function MemberOverviews({
	members,
	pendingInvitees,
	householdChildren,
	propertyId,
	entryApplicationId,
	canAddApplicant,
	canRemoveMember,
	canCancelInvite,
	canRemoveChild = false,
	onAddApplicant,
	onRemoveMember,
	onRemoveChild,
}: {
	members: GroupMember[]
	pendingInvitees: PendingInvitee[]
	/** Household children (household-max property step) — tiles only, no
	 * application behind them. */
	householdChildren: HouseholdChild[]
	propertyId: string
	entryApplicationId: string
	canAddApplicant: boolean
	/** `can.removeMember` (merge detach, or reject/withdraw decision exits) +
	 * decision open — gates the per-tile ✕. Removal DETACHES the member
	 * (group_id → NULL, never a delete). */
	canRemoveMember: boolean
	/** TT-42 (`can.cancelInvitation` + decision open) — "Cancel invite" on
	 * the invited tiles (2026-07-13). */
	canCancelInvite: boolean
	/** `can.removeChild` (applications.merge) — gates the child tiles' ✕
	 * (client 2026-09-07). Not decision-locked, like the member ✕. */
	canRemoveChild?: boolean
	onAddApplicant?: () => void
	onRemoveMember?: (member: GroupMember) => void
	onRemoveChild?: (child: HouseholdChild) => void
}) {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	// Household order (client 2026-08-27): primary, co-applicants, children,
	// guarantors, then everyone still invited — an invited co-applicant sits
	// last until they accept, then takes their normal place above.
	const applicants = [
		...members.filter((m) => m.applicationType === 'primary'),
		...members.filter((m) => m.applicationType === 'co_applicant'),
	]
	const guarantors = members.filter((m) => m.applicationType === 'guarantor')

	// View Report always opens the member's individual v2 page — a sibling
	// route of this combined page, so it's a real navigation even for the entry
	// application whose id is in the current URL.
	const view = (member: GroupMember) => () => {
		void navigate(
			preserveReturnTo(
				`/properties/${propertyId}/applications/${member.applicationId}/v2`,
				searchParams,
			),
		)
	}

	// Removal DETACHES a member (group_id → NULL); the server re-anchors a
	// removed primary and the controller navigates away from a removed entry
	// app, and an already-decided member can only be mistake-removed.
	// Every member — the PRIMARY included (client 2026-08-27) — carries the ✕
	// when the viewer may remove people. The one case that cannot happen (the
	// household's last undecided applicant, where removal IS the decision) is
	// still refused server-side, with a message telling the landlord to
	// decline or withdraw instead; hiding the control here made the primary
	// look permanently un-removable even in households where it works.
	const removeHandler = (member: GroupMember) => {
		if (!canRemoveMember || !onRemoveMember) return undefined
		return () => onRemoveMember(member)
	}

	return (
		<OverviewSection
			title="Applicant's Overview"
			action={
				canAddApplicant ? (
					<SectionAction
						icon={<Plus aria-hidden="true" />}
						label="Add Applicant"
						onClick={onAddApplicant}
					/>
				) : undefined
			}
			emptyNote="No applicants yet."
			hasContent={
				applicants.length +
					guarantors.length +
					householdChildren.length +
					pendingInvitees.length >
				0
			}
		>
			{applicants.map((member) => (
				<MemberTile
					key={member.applicationId}
					member={member}
					onView={view(member)}
					onRemove={removeHandler(member)}
				/>
			))}
			{householdChildren.map((child, index) => (
				<ChildTile
					key={`child-${child.name ?? ''}-${child.dateOfBirth ?? index}`}
					child={child}
					onRemove={
						canRemoveChild && onRemoveChild
							? () => onRemoveChild(child)
							: undefined
					}
				/>
			))}
			{guarantors.map((member) => (
				<MemberTile
					key={member.applicationId}
					member={member}
					onView={view(member)}
					onRemove={removeHandler(member)}
				/>
			))}
			{pendingInvitees.map((invitee) => (
				<InviteeTile
					key={invitee.id}
					invitee={invitee}
					cancelFromApplicationId={
						canCancelInvite ? entryApplicationId : undefined
					}
				/>
			))}
		</OverviewSection>
	)
}
